import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useServiceHook from '@hooks/useServiceHook';
import { VehicleService } from '@services/Vehicles/Vehicles';
import DropDownMenu from '@components/inputs/DropDownMenu';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import Label from '@components/inputs/text/Label';
import DatePicker from '@components/inputs/DateInput';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import VehicleDetails from '@components/panels/VehicleDetails';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Button from '@components/buttons/Button/Button';
import { dateToISOFormat } from '@utils/misc/functions';
import InfoBox from '@components/cards/messageBox/InfoBox';
import { Country } from '@utils/constants/localisation';
import { clearVehicleLocationChoice } from '@utils/constants/hubs';
import NumericInput from '@components/inputs/NumericInput';
import { countrySpecificDistanceUnit } from '@utils/misc/functions';
import * as Sentry from "@sentry/react";
import { SelectedSubscriptionState, SelectedSubscriptionAction } from "./selectedSubscriptionReducer";
import { CancellationVehicleState, CancellationVehicleAction } from "./cancellationVehicleReducer";

export interface IFleetioStatus {
    id: string,
    account_id: string,
    name: string,
    color: string,
};

type Props = {
  navigateTarget: string;
  selectedSubscriptionState: SelectedSubscriptionState;
  selectedSubscriptionDispatch: React.Dispatch<SelectedSubscriptionAction>;
  cancellationVehicleState: CancellationVehicleState;
  cancellationVehicleDispatch: React.Dispatch<CancellationVehicleAction>;
};

export default function CancellationVehicleDetails({
  navigateTarget,
  selectedSubscriptionState,
  selectedSubscriptionDispatch,
  cancellationVehicleState,
  cancellationVehicleDispatch
}: Props) {
    const todaysDate = dateToISOFormat(new Date());

    const navigate = useNavigate();
    const { custId, subsId, country } = useParams();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    // Only fetch vehicle statuses when we have a permanentVehicleId for the selected subscription
    const getVehicleDetails = Boolean(selectedSubscriptionState.permanentVehicleId);
    const [vehicleDetailsLoading, , , vehicleDetails] = useServiceHook(
        VehicleService.getVehicleById,
        [selectedSubscriptionState.permanentVehicleId, country],
        getVehicleDetails,
        [selectedSubscriptionState.permanentVehicleId, country]
    );



    const [statusLoading, , , statuses] = useServiceHook(
        VehicleService.getVehicleStatuses,
        ['returning', country],
    );

    // GB only: where the returning vehicle is being left (TP-6849). Fed by the same Fleetio
    // `vehicle_location` allowed-values list the onboarding hub filter uses, but asking the
    // opposite question — "where is this car going", not "show me cars already at this hub".
    // AU models location through Fleetio groups and has no such field, so the control is
    // not rendered and nothing is sent.
    const isGB = country === Country.GB;
    const [locationsLoading, , , vehicleLocations] = useServiceHook<string[]>(
        VehicleService.getVehicleLocations,
        [country],
        isGB,
        []
    );

    useEffect(() => {
        // Check if both vehicleDetails and statuses have been loaded
        if (statusLoading === false && vehicleDetailsLoading === false && statuses.length > 0) {
            // Save default values to the reducer when the component mounts
            cancellationVehicleDispatch({
              type: "SET_VEHICLE_ID",
              payload: vehicleDetails.id || ""
            });
            cancellationVehicleDispatch({
              type: "SET_REGISTRATION_PLATE",
              payload: vehicleDetails.license_plate || ""
            });
            cancellationVehicleDispatch({
              type: "SET_ENDING_KMS",
              payload: cancellationVehicleState.endingKms || vehicleDetails.current_meter_value || "0"
            });
            cancellationVehicleDispatch({
              type: "SET_FLEETIO_STATUS_ID",
              payload: cancellationVehicleState.fleetioStatusId || statuses[0].id
            });
            cancellationVehicleDispatch({
              type: "SET_FLEETIO_STATUS_NAME",
              payload: cancellationVehicleState.fleetioStatusName || statuses[0].name
            });
            cancellationVehicleDispatch({
              type: "SET_CANCELLATION_DATE",
              payload: cancellationVehicleState.cancellationDate || todaysDate
            });
            // GB has no Zoho Date_Submitted_Notice — seed from picker default so the charges
            // API and review screen see today's date even if the CSR doesn't touch the picker.
            if (country === Country.GB && !selectedSubscriptionState.noticeGiven) {
                selectedSubscriptionDispatch({
                  type: "SET_NOTICE_GIVEN",
                  payload: todaysDate
                });
            }
        }
    }, [statusLoading, vehicleDetailsLoading]);

    useEffect(  // when there is an ongoing request, disable the next step button
        () => setIsButtonDisabled(
            !(statusLoading === false && vehicleDetailsLoading === false)
            // GB must choose a return location (or "Blank") before moving on — there is no
            // plan-based exemption (TP-6849).
            || (isGB && !cancellationVehicleState.hub)
        ),
        [statusLoading, vehicleDetailsLoading, isGB, cancellationVehicleState.hub]
    );

    const onSelectStatus = (event: ChangeEvent<HTMLSelectElement>) => {
        const status = event.currentTarget.value;
        const selectedLabel = event.target.options[event.target.selectedIndex].label;
        cancellationVehicleDispatch({
          type: "SET_FLEETIO_STATUS_ID",
          payload: status
        });
        cancellationVehicleDispatch({
          type: "SET_FLEETIO_STATUS_NAME",
          payload: selectedLabel
        });
    };

    function convertDateFormat(inputDate: string) {
        const parts = inputDate.split('-');
        const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        return formattedDate;
    };

    const CancellationNotice = ({ country, noticeGiven }: { country: Country, noticeGiven: string | null }) => {
        if (country === Country.AU)
            return (<>
                <Label text='Notice given' styleCfg={{ bold: true }} />
                <p>
                    {noticeGiven === null
                        ? 'N/A'
                        : convertDateFormat(String(noticeGiven))}
                </p>
            </>);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
                <Label text='Notice given' styleCfg={{ bold: true }} />
                <InfoBox message='Please refer to emails and notes to find the date the customer informed Splend they wished to cancel.' />
                <div>
                    <DatePicker
                        name='noticeGiven'
                        defaultVal={selectedSubscriptionState.noticeGiven || todaysDate}
                        required={true}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            selectedSubscriptionDispatch({
                              type: "SET_NOTICE_GIVEN",
                              payload: e.target.value
                            })}
                    />
                </div>
            </div>
        );
    };

    const handleNext = () => {
      navigate(navigateTarget);
    };

    return (<>
        <NavBar pageTitle="Cancellation - Vehicle Details" />
        <div style={{ margin: '2.5vh 2.5vw' }}>
          <p>Please confirm that vehicle details are correct</p>
          {(vehicleDetailsLoading === false || vehicleDetailsLoading === null) && getVehicleDetails
              ? <VehicleDetails
                  make={vehicleDetails?.make || ''}
                  model={vehicleDetails?.model || ''}
                  year={vehicleDetails?.year || ''}
                  color={vehicleDetails?.color || ''}
                  licensePlate={vehicleDetails?.license_plate || ''}
                  vin={vehicleDetails?.vin || ''}
                  bodyType={vehicleDetails?.specs?.["body_type"] || ''}
                  startingMiles={vehicleDetails?.current_meter_value || ''}
              />
              : <LoadingSpinner />
          }
          {(vehicleDetailsLoading === false || vehicleDetailsLoading === null) && getVehicleDetails
              ? <Section childCmp={<>
                  <Label text= {`Ending ${countrySpecificDistanceUnit(String(country))}`}requiredFieldInd={true} />
                  <NumericInput
                      style={textBoxStyle}
                      name='endingKms'
                      dataTestId="endingKms"
                      defaultVal={cancellationVehicleState.endingKms || vehicleDetails?.current_meter_value || 0}
                      min={vehicleDetails?.current_meter_value}
                      step = "any"
                      required={true}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          cancellationVehicleDispatch({
                            type: "SET_ENDING_KMS",
                            payload: e.target.value
                          })}
                  />
                  <p>Please double check the odometer value in the vehicle as part of the cancellation process.</p>
              </>} />
              : <LoadingSpinner />
          }
          {statusLoading === false || statusLoading === null
              ? <Section childCmp={<>
                  <Label text='Fleetio status for outgoing vehicle' requiredFieldInd={true} />
                  <DropDownMenu
                      menuName='fleetioStatus'
                      required={true}
                      defaultVal={{value: cancellationVehicleState.fleetioStatusId, label: cancellationVehicleState.fleetioStatusName}}
                      choices={(statuses || []).map((item: IFleetioStatus) => ({
                              label: item.name,
                              value: item.id,
                              enabled: true
                          }))}
                      onSelect={(e: ChangeEvent<HTMLSelectElement>) => onSelectStatus(e)}
                  />
              </>} />
              : <LoadingSpinner />
          }

          {isGB && (locationsLoading === false || locationsLoading === null
              ? <Section childCmp={<>
                  <Label text='Vehicle return location' requiredFieldInd={true} />
                  <DropDownMenu
                      menuName='vehicleLocation'
                      testId='vehicleLocation'
                      required={true}
                      // No defaultVal until one is chosen, so DropDownMenu renders a blank
                      // first option and the CSR has to make a deliberate choice.
                      defaultVal={cancellationVehicleState.hub
                          ? { value: cancellationVehicleState.hub, label: cancellationVehicleState.hub }
                          : undefined}
                      choices={[
                          ...(vehicleLocations || []).map((location: string) => ({
                              label: location,
                              value: location,
                              enabled: true
                          })),
                          clearVehicleLocationChoice
                      ]}
                      onSelect={(e: ChangeEvent<HTMLSelectElement>) =>
                          cancellationVehicleDispatch({
                            type: "SET_HUB",
                            payload: e.currentTarget.value
                          })}
                  />
                  <p>Where the vehicle is being left. This is recorded in Fleetio so the vehicle can be found at the right site.</p>
              </>} />
              : <LoadingSpinner />)
          }

          <Section childCmp={<CancellationNotice country={country as Country} noticeGiven={selectedSubscriptionState.noticeGiven} />} />

          <Section childCmp={<>
              <Label text={'Cancellation Date'} requiredFieldInd={true} />
              <DatePicker
                  name='cancellationDate'
                  data-testid="cancellationDate"
                  defaultVal={cancellationVehicleState.cancellationDate || todaysDate}
                  required={true}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      cancellationVehicleDispatch({
                        type: "SET_CANCELLATION_DATE",
                        payload: e.target.value
                      })}
              />
          </>} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5vh 0', marginTop: '2.5vh' }}>
            <Button
              label="Next"
              onClickFunc={handleNext}
              disabled={isButtonDisabled}
            />
          </div>
        </div>
    </>);
};


const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
    <div style={{ margin: '2.5vh 0vw' }}>
        {childCmp}
    </div>
);


const LoadingSpinner = () => (
    <div style={{ height: '5vh' }}>
        <Elliptical />
    </div>
);